<template>
  <div class="car-detail" v-if="car">
    <!-- 详情页导航栏 -->
    <header class="detail-header">
      <div class="detail-nav-container">
        <div class="detail-nav-left">
          <button class="btn-back" @click="goBack">
            <i class="el-icon-arrow-left"></i>
            <span>{{ getText('nav.back') }}</span>
          </button>
          <div class="page-title">
            <h1>{{ getText('nav.vehicleDetail') }}</h1>
          </div>
        </div>
        <div class="detail-nav-right">
          <div class="language-switcher">
            <button
              v-for="lang in languages"
              :key="lang.code"
              :class="['lang-btn', { active: currentLang === lang.code }]"
              @click="switchLanguage(lang.code)"
            >
              {{ lang.name }}
            </button>
          </div>
          <button class="btn-favorite-large" @click="toggleFavorite">
            <i :class="isFavorite ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
            <span>{{ getText('nav.favorite') }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- 主要内容 -->
    <main class="main-content">
      <div class="container">
        <!-- 面包屑导航 -->
        <nav class="breadcrumb">
          <router-link to="/demo" class="breadcrumb-link">首页</router-link>
          <span class="breadcrumb-separator">></span>
          <span class="breadcrumb-current">{{ car.name }}</span>
        </nav>

        <!-- 车辆详情头部 -->
        <div class="car-header">
          <div class="car-gallery">
            <div class="main-image">
              <img :src="currentImage" :alt="car.name" />
              <div class="image-navigation">
                <button class="nav-btn prev" @click="prevImage" :disabled="currentImageIndex === 0">
                  <i class="el-icon-arrow-left"></i>
                </button>
                <button class="nav-btn next" @click="nextImage" :disabled="currentImageIndex === car.images.length - 1">
                  <i class="el-icon-arrow-right"></i>
                </button>
              </div>
            </div>
            <div class="thumbnail-gallery">
              <div
                v-for="(image, index) in car.images.slice(0, 5)"
                :key="index"
                class="thumbnail"
                :class="{ active: index === currentImageIndex }"
                @click="setCurrentImage(index)"
              >
                <img :src="image" :alt="`${car.name} ${index + 1}`" />
              </div>
            </div>
          </div>

          <div class="car-info">
            <div class="car-title-section">
              <h1 class="car-title">{{ car.name }}</h1>
              <div class="car-price">
                <span class="price">{{ car.price }}</span>
                <span class="price-unit">万</span>
              </div>
            </div>

            <div class="car-details">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ getText('car.registrationDate') }}</span>
                  <span class="detail-value">{{ car.year }}{{ getText('car.year') }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ getText('car.mileage') }}</span>
                  <span class="detail-value">{{ car.mileage }}{{ getText('car.kilometers') }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ getText('car.location') }}</span>
                  <span class="detail-value">{{ car.location }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">{{ getText('car.transmission') }}</span>
                  <span class="detail-value">{{ car.transmission }}</span>
                </div>
              </div>
            </div>

            <div class="car-actions">
              <button class="btn-primary" @click="showContactDialog">
                <i class="el-icon-phone"></i>
                {{ getText('car.contact') }}
              </button>
              <button class="btn-secondary" @click="scheduleTestDrive">
                <i class="el-icon-time"></i>
                {{ getText('car.testDrive') }}
              </button>
            </div>

            <div class="seller-card">
              <div class="seller-header">
                <img :src="car.seller.avatar" :alt="car.seller.name" class="seller-avatar" />
                <div class="seller-info">
                  <h4 class="seller-name">{{ car.seller.name }}</h4>
                  <p class="seller-title">{{ car.seller.title }}</p>
                </div>
              </div>
              <div class="seller-stats">
                <span class="stat-item">{{ getText('seller.deals') }} {{ car.seller.deals }}</span>
                <span class="stat-item">{{ getText('seller.rating') }} {{ car.seller.rating }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 车辆亮点 -->
        <div class="highlights-section">
          <h3 class="section-title">{{ getText('car.highlights') }}</h3>
          <div class="highlights-grid">
            <div class="highlight-card">
              <div class="highlight-icon">
                <i class="el-icon-check"></i>
              </div>
              <div class="highlight-content">
                <h4 class="highlight-title">{{ getText('highlights.condition.title') }}</h4>
                <p class="highlight-desc">{{ getText('highlights.condition.desc') }}</p>
              </div>
            </div>
            <div class="highlight-card">
              <div class="highlight-icon">
                <i class="el-icon-time"></i>
              </div>
              <div class="highlight-content">
                <h4 class="highlight-title">{{ getText('highlights.documents.title') }}</h4>
                <p class="highlight-desc">{{ getText('highlights.documents.desc') }}</p>
              </div>
            </div>
            <div class="highlight-card">
              <div class="highlight-icon">
                <i class="el-icon-price-tag"></i>
              </div>
              <div class="highlight-content">
                <h4 class="highlight-title">{{ getText('highlights.price.title') }}</h4>
                <p class="highlight-desc">{{ getText('highlights.price.desc') }}</p>
              </div>
            </div>
            <div class="highlight-card">
              <div class="highlight-icon">
                <i class="el-icon-service"></i>
              </div>
              <div class="highlight-content">
                <h4 class="highlight-title">{{ getText('highlights.service.title') }}</h4>
                <p class="highlight-desc">{{ getText('highlights.service.desc') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 车辆配置 -->
        <div class="specs-section">
          <h3 class="section-title">{{ getText('car.specs') }}</h3>
          <div class="specs-content">
            <div class="spec-category">
              <h4 class="category-title">{{ getText('car.basicInfo') }}</h4>
              <div class="spec-grid">
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.brandModel') }}</span>
                  <span class="spec-value">{{ car.brand }} {{ car.model }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.color') }}</span>
                  <span class="spec-value">{{ car.color }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.engine') }}</span>
                  <span class="spec-value">{{ car.engine }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.drive') }}</span>
                  <span class="spec-value">{{ car.drive }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.body') }}</span>
                  <span class="spec-value">{{ car.body }}</span>
                </div>
                <div class="spec-item">
                  <span class="spec-label">{{ getText('car.seats') }}</span>
                  <span class="spec-value">{{ car.seats }}{{ getText('car.seatsUnit') }}</span>
                </div>
              </div>
            </div>

            <div class="spec-category">
              <h4 class="category-title">{{ getText('car.specHighlights') }}</h4>
              <div class="features-showcase">
                <div
                  v-for="feature in car.detailedFeatures"
                  :key="feature.name"
                  class="feature-highlight"
                  :class="{ available: feature.available }"
                >
                  <i class="el-icon-check"></i>
                  <span>{{ feature.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 检测报告 -->
        <div class="inspection-section">
          <h3 class="section-title">{{ getText('car.inspection') }}</h3>
          <div class="inspection-content">
            <div class="inspection-main">
              <div class="score-display">
                <div class="score-circle">
                  <span class="score-number">{{ car.inspectionScore }}</span>
                  <span class="score-unit">分</span>
                </div>
                <div class="score-info">
                  <h4>{{ getText('car.score') }}</h4>
                  <p>{{ getText('inspection.description') }}</p>
                </div>
              </div>
            </div>
            <div class="inspection-breakdown">
              <div class="breakdown-item">
                <div class="breakdown-header">
                  <span class="breakdown-name">{{ getText('car.inspection.items.body') }}</span>
                  <span class="breakdown-score excellent">9.2</span>
                </div>
                <div class="breakdown-bar">
                  <div class="breakdown-fill excellent"></div>
                </div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-header">
                  <span class="breakdown-name">{{ getText('car.inspection.items.engine') }}</span>
                  <span class="breakdown-score good">8.5</span>
                </div>
                <div class="breakdown-bar">
                  <div class="breakdown-fill good"></div>
                </div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-header">
                  <span class="breakdown-name">{{ getText('car.inspection.items.transmission') }}</span>
                  <span class="breakdown-score excellent">9.0</span>
                </div>
                <div class="breakdown-bar">
                  <div class="breakdown-fill excellent"></div>
                </div>
              </div>
              <div class="breakdown-item">
                <div class="breakdown-header">
                  <span class="breakdown-name">{{ getText('car.inspection.items.chassis') }}</span>
                  <span class="breakdown-score good">8.7</span>
                </div>
                <div class="breakdown-bar">
                  <div class="breakdown-fill good"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- 联系对话框 -->
    <div v-if="showContact" class="modal-overlay" @click="closeContactDialog">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>联系卖家</h3>
          <button class="close-btn" @click="closeContactDialog">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="contact-form">
            <div class="form-group">
              <label>您的姓名</label>
              <input type="text" v-model="contactForm.name" placeholder="请输入您的姓名" />
            </div>
            <div class="form-group">
              <label>联系电话</label>
              <input type="tel" v-model="contactForm.phone" placeholder="请输入联系电话" />
            </div>
            <div class="form-group">
              <label>咨询内容</label>
              <textarea v-model="contactForm.message" placeholder="请描述您的需求..." rows="4"></textarea>
            </div>
            <button class="btn-primary full-width" @click="submitContact">提交咨询</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片灯箱 -->
    <div v-if="lightboxOpen" class="lightbox-overlay" @click="closeLightbox">
      <div class="lightbox-content">
        <img :src="lightboxImage" :alt="car.name" />
        <button class="lightbox-close" @click="closeLightbox">
          <i class="el-icon-close"></i>
        </button>
        <button class="lightbox-nav prev" @click="lightboxPrev" :disabled="lightboxIndex === 0">
          <i class="el-icon-arrow-left"></i>
        </button>
        <button class="lightbox-nav next" @click="lightboxNext" :disabled="lightboxIndex === car.images.length - 1">
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
    </div>
  </div>
  <div v-else class="loading">
    <div class="loading-spinner"></div>
    <p>正在加载车辆信息...</p>
  </div>
</template>

<script>
export default {
  name: 'CarDetail',
  data() {
    return {
      car: null,
      currentImageIndex: 0,
      activeTab: 'basic',
      isFavorite: false,
      showContact: false,
      lightboxOpen: false,
      lightboxIndex: 0,
      currentLang: localStorage.getItem('language') || 'zh',
      languages: [
        { code: 'zh', name: '中文' },
        { code: 'en', name: 'EN' },
        { code: 'ru', name: 'РУ' }
      ],
      translations: {
        zh: {
          nav: { back: '返回', vehicleDetail: '车辆详情', favorite: '收藏' },
          car: {
            specs: '车辆配置',
            highlights: '车辆亮点',
            inspection: '检测报告',
            contact: '联系卖家',
            testDrive: '预约试驾',
            registrationDate: '上牌时间',
            mileage: '行驶里程',
            location: '车辆所在地',
            transmission: '变速箱',
            year: '年',
            kilometers: '万公里',
            fuel: '燃油',
            body: '车身',
            seats: '座位',
            emission: '排放',
            color: '颜色',
            engine: '发动机',
            drive: '驱动',
            basicInfo: '基本信息',
            brandModel: '品牌型号',
            seatsUnit: '座',
            detailedSpecs: '详细规格',
            inspectionReport: '检测报告',
            score: '综合评分',
            excellent: '优秀',
            good: '良好',
            fair: '一般',
            poor: '较差'
          },
          seller: {
            deals: '成交',
            rating: '好评率'
          },
          highlights: {
            condition: {
              title: '车况良好',
              desc: '经过专业检测，所有系统运行正常'
            },
            documents: {
              title: '手续齐全',
              desc: '提供完整的车辆档案和过户手续'
            },
            price: {
              title: '价格合理',
              desc: '市场调研后确定最具竞争力的价格'
            },
            service: {
              title: '售后保障',
              desc: '提供365天售后服务和技术支持'
            }
          },
          specHighlights: '配置亮点',
          inspection: {
            description: '经过152项专业检测，质量有保障',
            items: {
              body: '车身骨架',
              engine: '发动机',
              transmission: '变速箱',
              chassis: '底盘悬挂'
            }
          }
        },
        en: {
          nav: { back: 'Back', vehicleDetail: 'Vehicle Detail', favorite: 'Favorite' },
          car: {
            specs: 'Specifications',
            highlights: 'Highlights',
            inspection: 'Inspection Report',
            contact: 'Contact Seller',
            testDrive: 'Book Test Drive',
            registrationDate: 'Registration Date',
            mileage: 'Mileage',
            location: 'Location',
            transmission: 'Transmission',
            year: '',
            kilometers: ' km',
            fuel: 'Fuel',
            body: 'Body',
            seats: 'Seats',
            emission: 'Emission',
            color: 'Color',
            engine: 'Engine',
            drive: 'Drive',
            basicInfo: 'Basic Information',
            brandModel: 'Brand & Model',
            seatsUnit: ' seats',
            detailedSpecs: 'Detailed Specifications',
            inspectionReport: 'Inspection Report',
            score: 'Overall Score',
            excellent: 'Excellent',
            good: 'Good',
            fair: 'Fair',
            poor: 'Poor'
          },
          seller: {
            deals: 'Deals',
            rating: 'Rating'
          },
          highlights: {
            condition: {
              title: 'Excellent Condition',
              desc: 'Professionally inspected, all systems operating normally'
            },
            documents: {
              title: 'Complete Documentation',
              desc: 'Full vehicle records and transfer documents provided'
            },
            price: {
              title: 'Competitive Price',
              desc: 'Market research to determine the most competitive price'
            },
            service: {
              title: 'After-sales Support',
              desc: '365 days after-sales service and technical support'
            }
          },
          specHighlights: 'Specification Highlights',
          inspection: {
            description: '152 professional inspections ensure quality guarantee',
            items: {
              body: 'Body Frame',
              engine: 'Engine',
              transmission: 'Transmission',
              chassis: 'Chassis Suspension'
            }
          }
        },
        ru: {
          nav: { back: 'Назад', vehicleDetail: 'Детали автомобиля', favorite: 'Избранное' },
          car: {
            specs: 'Характеристики',
            highlights: 'Особенности',
            inspection: 'Отчет об осмотре',
            contact: 'Связаться с продавцом',
            testDrive: 'Записаться на тест-драйв',
            registrationDate: 'Дата регистрации',
            mileage: 'Пробег',
            location: 'Местоположение',
            transmission: 'Коробка передач',
            year: ' г.',
            kilometers: ' км',
            fuel: 'Топливо',
            body: 'Кузов',
            seats: 'Мест',
            emission: 'Выбросы',
            color: 'Цвет',
            engine: 'Двигатель',
            drive: 'Привод',
            basicInfo: 'Основная информация',
            brandModel: 'Марка и модель',
            seatsUnit: ' мест',
            detailedSpecs: 'Подробные характеристики',
            inspectionReport: 'Отчет об осмотре',
            score: 'Общий рейтинг',
            excellent: 'Отлично',
            good: 'Хорошо',
            fair: 'Удовлетворительно',
            poor: 'Плохо'
          },
          seller: {
            deals: 'Сделок',
            rating: 'Рейтинг'
          },
          highlights: {
            condition: {
              title: 'Отличное состояние',
              desc: 'Профессионально осмотрен, все системы работают нормально'
            },
            documents: {
              title: 'Полная документация',
              desc: 'Предоставлены полные документы автомобиля и перерегистрации'
            },
            price: {
              title: 'Конкурентная цена',
              desc: 'Исследование рынка для определения самой конкурентной цены'
            },
            service: {
              title: 'Послепродажная поддержка',
              desc: '365 дней послепродажного обслуживания и технической поддержки'
            }
          },
          specHighlights: 'Особенности комплектации',
          inspection: {
            description: '152 профессиональные проверки обеспечивают гарантию качества',
            items: {
              body: 'Кузов',
              engine: 'Двигатель',
              transmission: 'Трансмиссия',
              chassis: 'Подвеска'
            }
          }
        }
      },
      contactForm: {
        name: '',
        phone: '',
        message: ''
      },
      tabs: [
        { id: 'basic', name: '基本信息' },
        { id: 'features', name: '车辆配置' },
        { id: 'inspection', name: '检测报告' },
        { id: 'gallery', name: '车辆相册' }
      ]
    }
  },
  computed: {
    currentImage() {
      return this.car ? this.car.images[this.currentImageIndex] : ''
    },
    lightboxImage() {
      return this.car ? this.car.images[this.lightboxIndex] : ''
    },
    recommendedCars() {
      // 这里应该从API获取相似推荐车辆
      return [
        {
          id: 3,
          name: '奥迪A4L 40TFSI',
          price: '¥25.9万',
          image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=200&fit=crop'
        },
        {
          id: 5,
          name: '本田XR-V 1.5T',
          price: '¥12.8万',
          image: 'https://images.unsplash.com/photo-1549399735-cef2e2c3f638?w=300&h=200&fit=crop'
        },
        {
          id: 6,
          name: '大众朗逸 1.6L',
          price: '¥10.9万',
          image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=300&h=200&fit=crop'
        }
      ]
    }
  },
  methods: {
    getText(path) {
      const keys = path.split('.');
      let value = this.translations[this.currentLang];
      for (const key of keys) {
        value = value && value[key];
      }
      return value || path;
    },
    switchLanguage(lang) {
      this.currentLang = lang;
      localStorage.setItem('language', lang);
      this.$forceUpdate();
    },
    goBack() {
      this.$router.push('/demo');
    },
    loadCarData() {
      const carId = parseInt(this.$route.params.id)
      // 这里应该从API获取车辆数据
      const mockCarData = {
        id: carId,
        name: '奔驰C级 C260L',
        brand: '奔驰',
        model: 'C级',
        price: '28.8',
        originalPrice: '¥32万',
        year: '2018',
        mileage: '6.8',
        location: '北京',
        badge: '超值',
        color: '珍珠白',
        engine: '2.0T 184马力 L4',
        drive: '后轮驱动',
        body: '三厢轿车',
        seats: '5',
        emission: '国五',
        transmission: '自动',
        fuel: '汽油',
        images: [
          'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=800&h=600&fit=crop&auto=format',
          'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&h=600&fit=crop&auto=format',
          'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop&auto=format',
          'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&h=600&fit=crop&auto=format',
          'https://images.unsplash.com/photo-1544829099-b9a0e3421dbb?w=800&h=600&fit=crop&auto=format',
          'https://images.unsplash.com/photo-1549399735-cef2e2c3f638?w=800&h=600&fit=crop&auto=format'
        ],
        detailedFeatures: [
          { name: '天窗', available: true },
          { name: '导航系统', available: true },
          { name: '真皮座椅', available: true },
          { name: '自动空调', available: true },
          { name: '倒车影像', available: true },
          { name: '定速巡航', available: true },
          { name: 'LED大灯', available: true },
          { name: '电动座椅', available: false },
          { name: '全景影像', available: false },
          { name: '自动泊车', available: false },
          { name: '驾驶辅助', available: true },
          { name: '车道保持', available: false }
        ],
        inspectionScore: '8.5',
        seller: {
          name: '张经理',
          title: '资深销售顾问',
          avatar: 'https://via.placeholder.com/60x60/2196f3/ffffff?text=Z',
          deals: '156',
          rating: '98'
        }
      }
      this.car = mockCarData
    },
    setCurrentImage(index) {
      this.currentImageIndex = index
    },
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
      }
    },
    nextImage() {
      if (this.currentImageIndex < this.car.images.length - 1) {
        this.currentImageIndex++
      }
    },
    toggleFavorite() {
      this.isFavorite = !this.isFavorite
      // 这里应该调用API保存收藏状态
    },
    showContactDialog() {
      this.showContact = true
    },
    closeContactDialog() {
      this.showContact = false
    },
    scheduleTestDrive() {
      this.$message.success('试驾预约已提交，我们将尽快联系您！')
    },
    addToComparison() {
      this.$message.success('已添加到对比列表')
    },
    callSeller() {
      window.location.href = 'tel:400-888-8888'
    },
    sendMessage() {
      this.showContactDialog()
    },
    submitContact() {
      // 验证表单
      if (!this.contactForm.name || !this.contactForm.phone) {
        this.$message.error('请填写完整信息')
        return
      }
      // 这里应该调用API提交表单
      this.$message.success('咨询已提交，卖家将尽快联系您！')
      this.closeContactDialog()
    },
    openLightbox(index) {
      this.lightboxIndex = index
      this.lightboxOpen = true
    },
    closeLightbox() {
      this.lightboxOpen = false
    },
    lightboxPrev() {
      if (this.lightboxIndex > 0) {
        this.lightboxIndex--
      }
    },
    lightboxNext() {
      if (this.lightboxIndex < this.car.images.length - 1) {
        this.lightboxIndex++
      }
    },
    downloadReport() {
      this.$message.info('检测报告下载功能即将上线')
    },
    goToCarDetail(carId) {
      this.$router.push({ name: 'CarDetail', params: { id: carId } })
    }
  },
  created() {
    this.loadCarData()
  },
  watch: {
    '$route.params.id'() {
      this.loadCarData()
    }
  }
}
</script>

<style scoped>
.car-detail {
  min-height: 100vh;
  background: #fafbfc;
}

/* 详情页导航栏样式 */
.detail-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #e5e7eb;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.detail-nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.detail-nav-left {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(0, 0, 0, 0.1);
  border-color: #d1d5db;
}

.page-title h1 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.detail-nav-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.language-switcher {
  display: flex;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-right: 1rem;
}

.lang-btn {
  padding: 0.375rem 0.75rem;
  background: #ffffff;
  border: none;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-btn:hover {
  background: #f8fafc;
  color: #374151;
}

.lang-btn.active {
  background: #2563eb;
  color: #ffffff;
}

.btn-favorite-large {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.btn-favorite-large:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

/* 顶部导航栏 */
.header {
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e2e8f0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-favorite {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: auto;
  height: 36px;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-favorite:hover {
  background: #fed7d7;
  transform: scale(1.05);
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea, #764ba2);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.nav-menu {
  display: flex;
  gap: 2rem;
}

.nav-link {
  text-decoration: none;
  color: #6b7280;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;
}

.nav-link:hover {
  color: #667eea;
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -5px;
  left: 0;
  background: linear-gradient(135deg, #667eea, #764ba2);
  transition: width 0.3s ease;
}

.nav-link:hover::after {
  width: 100%;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn-favorite {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #e53e3e;
  font-size: 1.2rem;
}

.btn-favorite:hover {
  background: #fed7d7;
  transform: scale(1.1);
}

/* 主要内容 */
.main-content {
  padding-top: 90px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

/* 面包屑导航 */
.breadcrumb {
  padding: 2rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.breadcrumb-link {
  color: #667eea;
  text-decoration: none;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: #a0aec0;
}

.breadcrumb-current {
  color: #4a5568;
  font-weight: 500;
}

/* 车辆详情头部 */
.car-header {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 3rem;
  margin-bottom: 3rem;
}

.car-gallery {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.main-image {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.main-image img {
  width: 100%;
  height: 400px;
  object-fit: cover;
}

.image-navigation {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  padding: 0 1rem;
}

.nav-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #4a5568;
}

.nav-btn:hover:not(:disabled) {
  background: #ffffff;
  transform: scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.thumbnail-gallery {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem 0;
}

.thumbnail {
  flex-shrink: 0;
  width: 80px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.thumbnail.active {
  border-color: #667eea;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail:hover {
  transform: scale(1.05);
}

.car-summary {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.car-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.car-title h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.car-badge {
  background: linear-gradient(135deg, #e53e3e, #ed8936);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.car-price-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.current-price {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #e53e3e;
}

.price-unit {
  font-size: 1.5rem;
  color: #e53e3e;
  font-weight: 600;
}

.original-price {
  font-size: 1rem;
  color: #a0aec0;
  text-decoration: line-through;
}

.car-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-label {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

.meta-value {
  font-size: 1rem;
  color: #1a202c;
  font-weight: 600;
}

.car-actions {
  display: flex;
  gap: 1rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.25);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.35);
}

.btn-secondary {
  background: transparent;
  color: #4a5568;
  border: 2px solid #e2e8f0;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-secondary:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.btn-outline {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-outline:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.contact-info {
  background: #ffffff;
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.seller-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.seller-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
}

.seller-details h4 {
  margin: 0 0 0.25rem 0;
  color: #1a202c;
  font-size: 1.125rem;
}

.seller-details p {
  margin: 0 0 0.5rem 0;
  color: #718096;
  font-size: 0.875rem;
}

.seller-stats {
  display: flex;
  gap: 1rem;
  font-size: 0.75rem;
  color: #a0aec0;
}

.contact-methods {
  display: flex;
  gap: 1rem;
}

.contact-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.contact-btn.phone {
  background: linear-gradient(135deg, #48bb78, #38a169);
  color: white;
  border: none;
}

.contact-btn.phone:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
}

.contact-btn.message {
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  border: none;
}

.contact-btn.message:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
}

/* 标签页 */
.car-details-tabs {
  margin-bottom: 4rem;
}

.tabs-nav {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 2rem;
}

.tab-btn {
  background: transparent;
  border: none;
  padding: 1rem 2rem;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
}

.tab-btn.active {
  color: #667eea;
  border-bottom-color: #667eea;
}

.tab-btn:hover {
  color: #4a5568;
}

.tab-content {
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
}

.tab-panel h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 2rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
}

.info-label {
  color: #718096;
  font-weight: 500;
}

.info-value {
  color: #1a202c;
  font-weight: 600;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.feature-item.available {
  background: #f0fff4;
  color: #22543d;
}

.feature-item:not(.available) {
  background: #fed7d7;
  color: #742a2a;
}

.feature-item i {
  font-size: 1.125rem;
}

.inspection-report {
  display: flex;
  gap: 3rem;
  align-items: flex-start;
}

.inspection-summary {
  flex: 1;
  display: flex;
  gap: 2rem;
}

.score-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.score {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
}

.score-label {
  font-size: 0.875rem;
  color: #718096;
}

.inspection-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f7fafc;
  border-radius: 8px;
}

.detail-label {
  color: #4a5568;
  font-weight: 500;
}

.detail-value.excellent {
  color: #22543d;
  background: #c6f6d5;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
}

.detail-value.good {
  color: #744210;
  background: #fef5e7;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
}

.report-download {
  display: flex;
  align-items: center;
}

.full-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.gallery-item {
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.gallery-item:hover {
  transform: scale(1.05);
}

.gallery-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

/* 推荐车辆 */
.recommended-cars {
  margin-bottom: 4rem;
}

.recommended-cars h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 2rem;
}

.recommended-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.recommended-card {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
}

.recommended-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.recommended-card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.rec-info {
  padding: 1rem;
}

.rec-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.rec-price {
  font-size: 1.125rem;
  font-weight: 700;
  color: #e53e3e;
}

/* 模态框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  color: #1a202c;
  font-size: 1.25rem;
  font-weight: 700;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: #a0aec0;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #f7fafc;
  color: #4a5568;
}

.modal-body {
  padding: 2rem;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.875rem;
}

.form-group input,
.form-group textarea {
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.full-width {
  width: 100%;
}

/* 灯箱 */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 1.5rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.lightbox-nav:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-50%) scale(1.1);
}

.lightbox-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lightbox-nav.prev {
  left: -70px;
}

.lightbox-nav.next {
  right: -70px;
}

/* 加载状态 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 2rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading p {
  color: #718096;
  font-size: 1.125rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 移动端适配 */

@media (max-width: 768px) {
  .detail-header {
    padding: 0.5rem 0.75rem;
  }

  .detail-nav-container {
    padding: 0;
  }

  .detail-nav-left {
    gap: 0.5rem;
  }

  .btn-back span {
    display: none;
  }

  .page-title {
    display: none;
  }

  .detail-nav-right {
    gap: 0.25rem;
  }

  .language-switcher {
    display: none;
  }

  .btn-favorite-large {
    padding: 0.5rem 0.625rem;
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .car-header {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .car-gallery {
    order: -1;
  }

  .main-image img {
    height: 300px;
  }
}

@media (max-width: 768px) {
  .detail-header {
    padding: 0.5rem 1rem;
  }

  .detail-nav-container {
    padding: 0;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .detail-nav-left {
    gap: 0.5rem;
    min-width: 0;
    flex: 1;
  }

  .btn-back {
    padding: 0.5rem;
    font-size: 0.8125rem;
  }

  .btn-back span {
    display: none;
  }

  .page-title {
    display: none;
  }

  .detail-nav-right {
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .language-switcher {
    display: none;
  }

  .btn-favorite-large {
    padding: 0.5rem;
    font-size: 0.8125rem;
  }

  .container {
    padding: 0 1rem;
  }

  .car-title h1 {
    font-size: 1.25rem;
  }

  .price {
    font-size: 1.75rem;
  }

  .car-meta-grid {
    gap: 0.5rem;
  }

  .car-meta-item {
    padding: 0.75rem;
  }

  .car-actions {
    gap: 0.5rem;
  }

  .contact-methods {
    gap: 0.5rem;
  }

  .inspection-report {
    gap: 1.5rem;
  }

  .inspection-item {
    padding: 0.75rem;
  }

  .full-gallery {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.5rem;
  }

  .recommended-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .highlight-grid {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 0.5rem;
  }

  .highlight-item {
    padding: 0.5rem;
  }

  .specs-grid {
    gap: 0.5rem;
  }

  .spec-item {
    padding: 0.5rem;
  }

  .tabs-nav {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .tab-btn {
    white-space: nowrap;
    padding: 0.75rem 1rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}

/* 简化的详情页样式 */
.car-title-section {
  margin-bottom: 1rem;
}

.car-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.car-price {
  display: flex;
  align-items: baseline;
  gap: 0.125rem;
}

.price {
  font-size: 2rem;
  font-weight: 700;
  color: #dc2626;
}

.price-unit {
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
}

.detail-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  font-size: 0.875rem;
  color: #1a202c;
  font-weight: 600;
}

.car-actions {
  display: flex;
  gap: 1rem;
  margin: 1.5rem 0;
}

.btn-primary, .btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.btn-secondary {
  background: transparent;
  color: #4a5568;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.seller-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.seller-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.seller-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.seller-info h4 {
  margin: 0 0 0.125rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
}

.seller-info p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.seller-stats {
  display: flex;
  gap: 1rem;
}

.stat-item {
  font-size: 0.8125rem;
  color: #64748b;
  font-weight: 500;
}

/* 车辆亮点区域 */
.highlights-section {
  margin-bottom: 3rem;
}

.highlights-section .section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1.5rem;
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.highlight-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: all 0.3s ease;
}

.highlight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  background: #ffffff;
}

.highlight-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.highlight-content h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
}

.highlight-content p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.5;
}

/* 车辆规格区域 */
.specs-section {
  margin-bottom: 3rem;
}

.specs-section .section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1.5rem;
}

.specs-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.spec-category {
  background: #f8fafc;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #e5e7eb;
}

.category-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1.5rem;
  position: relative;
}

.category-title::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 32px;
  height: 2px;
  background: #2563eb;
  border-radius: 1px;
}

.spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
}

.spec-label {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.spec-value {
  font-size: 0.875rem;
  color: #1a202c;
  font-weight: 600;
}

.features-showcase {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}

.feature-highlight {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
}

.feature-highlight.available {
  background: #f0fdf4;
  color: #166534;
  border-color: #bbf7d0;
}

.feature-highlight i {
  color: #10b981;
  font-size: 1rem;
}

/* 检测报告区域 */
.inspection-section {
  margin-bottom: 3rem;
}

.inspection-section .section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1.5rem;
}

.inspection-content {
  background: #f8fafc;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #e5e7eb;
}

.inspection-main {
  margin-bottom: 2rem;
}

.score-display {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.score-circle {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
}

.score-number {
  font-size: 1.5rem;
  font-weight: 700;
}

.score-unit {
  font-size: 0.75rem;
  margin-left: 2px;
}

.score-info h4 {
  margin: 0 0 0.25rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
}

.score-info p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.inspection-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.breakdown-item {
  background: #ffffff;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e5e7eb;
}

.breakdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.breakdown-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
}

.breakdown-score {
  font-size: 1rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  color: white;
}

.breakdown-score.excellent {
  background: #10b981;
}

.breakdown-score.good {
  background: #f59e0b;
}

.breakdown-bar {
  height: 6px;
  background: #f1f5f9;
  border-radius: 3px;
  overflow: hidden;
}

.breakdown-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.breakdown-fill.excellent {
  background: #10b981;
}

.breakdown-fill.good {
  background: #f59e0b;
}
</style>
